<template>
  <div>
    <div class="card overflow-hidden">
      <div class="card-body position-relative">
        <div class="text-center mt-3">
          <div class="chat-avtar d-inline-flex mx-auto position-relative">
            <!-- Profile picture with initial loading state -->
            <div 
              v-if="isProfilePicLoading"
              class="profile-avatar-skeleton rounded-circle img-thumbnail d-flex align-items-center justify-content-center"
            >
              <div class="spinner-border spinner-border-sm text-primary" role="status">
                <span class="visually-hidden">{{ $t('common.loading') }}</span>
              </div>
            </div>
            <img 
              v-else
              :src="profilePictureUrl || defaultAvatar" 
              class="profile-avatar rounded-circle img-thumbnail"
              alt="User profile"
              ref="profilePicture" />
            <i class="chat-badge bg-success me-2 mb-2"></i>
            
            <!-- Upload overlay -->
            <div class="profile-pic-upload" @click="triggerFileInput">
              <i class="ti ti-camera"></i>
            </div>
            
            <!-- File input (hidden) -->
            <input 
              type="file" 
              ref="fileInput" 
              class="d-none" 
              accept="image/*"
              @change="handleFileSelected" />
              
            <!-- Loading overlay -->
            <div class="profile-pic-loading" v-if="isUploading">
              <div class="spinner-border text-light spinner-border-sm" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
          
          <h5 class="mb-0">{{ userDisplayName }}</h5>
          <p class="text-muted text-sm">{{ $t('profile.role.supplier') }}</p>
          
          <div class="row g-3 mt-3">
            <div class="col-6">
              <h5 class="mb-0" v-if="isLoadingCounts">
                <div class="spinner-border spinner-border-sm text-primary" role="status">
                  <span class="visually-hidden">{{ $t('common.loading') }}</span>
                </div>
              </h5>
              <h5 class="mb-0" v-else>{{ entityCounts.certificatesCount || 0 }}</h5>
              <small class="text-muted">{{ $t('profile.counts.certificates') || 'Certificates' }}</small>
            </div>
            <!-- TODO: v2 - Temporarily hidden Categories count section -->
            <!-- <div class="col-4 border border-top-0 border-bottom-0">
              <h5 class="mb-0" v-if="isLoadingCounts">
                <div class="spinner-border spinner-border-sm text-primary" role="status">
                  <span class="visually-hidden">{{ $t('common.loading') }}</span>
                </div>
              </h5>
              <h5 class="mb-0" v-else>{{ entityCounts.materialCategoriesCount || 0 }}</h5>
              <small class="text-muted">{{ $t('profile.counts.categories') || 'Categories' }}</small>
            </div> -->
            <div class="col-6">
              <h5 class="mb-0" v-if="isLoadingCounts">
                <div class="spinner-border spinner-border-sm text-primary" role="status">
                  <span class="visually-hidden">{{ $t('common.loading') }}</span>
                </div>
              </h5>
              <h5 class="mb-0" v-else>{{ entityCounts.portsCount || 0 }}</h5>
              <small class="text-muted">{{ $t('profile.counts.ports') || 'Ports' }}</small>
            </div>
          </div>
        </div>
      </div>
      <div class="nav flex-column nav-pills list-group list-group-flush account-pills mb-0" id="user-set-tab"
        role="tablist" aria-orientation="vertical">
        <a class="nav-link list-group-item list-group-item-action active" id="user-set-profile-tab"
          data-bs-toggle="pill" href="#user-set-profile" role="tab" aria-controls="user-set-profile"
          aria-selected="true">
          <span class="f-w-500"><i class="ti ti-user-circle m-r-10"></i>{{ $t('profile.tabs.overview') }}</span>
        </a>
        <a class="nav-link list-group-item list-group-item-action" id="user-set-account-tab" data-bs-toggle="pill"
          href="#user-set-account" role="tab" aria-controls="user-set-account" aria-selected="false">
          <span class="f-w-500"><i class="ti ti-notebook m-r-10"></i>{{ $t('profile.tabs.account') }}</span>
        </a>
        
      </div>
    </div>
    
    <profile-port-card 
      :can-assign-port-to-tenant="portPermissions.canAssignPortToTenant()"
      :can-edit-ports="portPermissions.canEditPorts()"
      :can-delete-ports="portPermissions.canDeletePorts()"
      @ports-updated="loadEntityCounts"
    />
    
    <profile-company-card :profile="profile" :is-loading="isLoading" />
  </div>
</template>

<script>
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { usePortPermissions } from '@/composables/usePortPermissions';
import ProfilePortCard from './ProfilePortCard.vue';
import ProfileCompanyCard from './ProfileCompanyCard.vue';
import fileManagementService from '@/services/fileManagementService';
import tenantInfoService from '@/services/tenantInfoService';
import defaultAvatarSrc from '@/assets/images/logo/vesproc40x40.png';

export default {
  name: 'ProfileSidebar',
  components: {
    ProfilePortCard,
    ProfileCompanyCard
  },
  props: {
    currentUser: {
      type: Object,
      required: true
    },
    profile: {
      type: Object,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const store = useStore();
    const { t } = useI18n();
    const fileInput = ref(null);
    const profilePicture = ref(null);
    const profilePictureUrl = ref('');
    const isProfilePicLoading = ref(true);
    const isUploading = ref(false);
    const isLoadingCounts = ref(false);
    const defaultAvatar = defaultAvatarSrc;
    
    // Get entity counts directly from the store
    const entityCounts = computed(() => store.getters.getEntityCounts);
    
    const userDisplayName = computed(() => {
      // Show impersonation information if applicable
      if (props.currentUser?.isImpersonating) {
        if (props.currentUser.isImpersonatingTenant) {
          const tenantName = props.currentUser.newTenantName || 
                             props.currentUser.impersonatedTenant?.name || 
                             'Impersonated Tenant';
          return `${tenantName} (Impersonating)`;
        } else if (props.currentUser.impersonatedUser) {
          return `${props.currentUser.impersonatedUser.email} (Impersonating)`;
        }
      }
      
      // Use company display name from tenant extra properties if available
      if (props.profile?.displayName) {
        return props.profile.displayName;
      }
      
      // Fallback to current user name if no company display name
      if (props.currentUser?.firstName && props.currentUser?.lastName) {
        return `${props.currentUser.firstName} ${props.currentUser.lastName}`;
      }
      
      return props.currentUser?.email || 'Company Profile';
    });
    
    // Keep track of created blob URLs to clean up later
    const createdBlobUrls = [];
    
    // Load entity counts from API
    const loadEntityCounts = async () => {
      isLoadingCounts.value = true;
      try {
        const response = await tenantInfoService.getEntityCounts();
        if (response) {
          // Update store instead of local ref
          store.commit('updateEntityCounts', response);
        }
      } catch (error) {
        console.error('Error loading entity counts:', error);
      } finally {
        isLoadingCounts.value = false;
      }
    };
    
    // Load profile picture
    const loadProfilePicture = async () => {
      isProfilePicLoading.value = true;
      try {
        const response = await fileManagementService.getProfilePicture();
        if (response.success && response.blobUrl) {
          // Revoke previous URL if it exists to avoid memory leaks
          if (profilePictureUrl.value && profilePictureUrl.value !== defaultAvatar) {
            URL.revokeObjectURL(profilePictureUrl.value);
          }
          
          profilePictureUrl.value = response.blobUrl;
          createdBlobUrls.push(response.blobUrl);
        } else {
          profilePictureUrl.value = defaultAvatar;
        }
      } catch (error) {
        console.error('Error loading profile picture:', error);
        profilePictureUrl.value = defaultAvatar;
      } finally {
        isProfilePicLoading.value = false;
      }
    };
    
    // Trigger file input click
    const triggerFileInput = () => {
      if (fileInput.value) {
        fileInput.value.click();
      }
    };
    
    // Handle file selection
    const handleFileSelected = async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;
      
      // Check if file is an image
      if (!file.type.match('image.*')) {
        store.dispatch('notifications/addNotification', {
          type: 'error',
          message: t('profile.picture.invalidType')
        });
        return;
      }
      
      // Check file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        store.dispatch('notifications/addNotification', {
          type: 'error',
          message: t('profile.picture.sizeError')
        });
        return;
      }
      
      isUploading.value = true;
      
      // Upload profile picture using the service
      fileManagementService.uploadProfilePicture(file)
        .then(response => {
          if (response.success) {
            store.dispatch('notifications/addNotification', {
              type: 'success',
              message: t('profile.picture.uploadSuccess')
            });
            
            // Reload profile picture after successful upload
            loadProfilePicture();

            // Emit a global event so other components (e.g., header) can refresh avatars
            try {
              window.dispatchEvent(new CustomEvent('profile-picture-updated'));
            } catch (e) {
              console.warn('Could not dispatch profile-picture-updated event', e);
            }
          } else {
            throw new Error('Upload failed');
          }
        })
        .catch(error => {
          console.error('Error uploading profile picture:', error);
          store.dispatch('notifications/addNotification', {
            type: 'error',
            message: t('profile.picture.uploadError')
          });
        })
        .finally(() => {
          isUploading.value = false;
          // Clear the file input so the same file can be selected again
          if (event.target) {
            event.target.value = '';
          }
        });
    };
    
    // Load profile picture and entity counts on component mount
    onMounted(() => {
      console.log('🚀 ProfileSidebar.vue: Component mounted, props.profile:', props.profile);
      loadProfilePicture();
      loadEntityCounts();
    });
    
    // Watch for changes in profile prop
    watch(() => props.profile, (newProfile, oldProfile) => {
      console.log('👀 ProfileSidebar.vue: Profile prop changed:', { newProfile, oldProfile });
      
      // Only reload profile picture if companyLogoId changed
      if (newProfile?.companyLogoId !== oldProfile?.companyLogoId) {
        loadProfilePicture();
      }
    }, { deep: true });
    
    // Clean up blob URLs when component unmounts
    onBeforeUnmount(() => {
      // Revoke all created blob URLs to avoid memory leaks
      createdBlobUrls.forEach(url => {
        if (url && url !== defaultAvatar && typeof url === 'string') {
          try {
            URL.revokeObjectURL(url);
          } catch (e) {
            console.error('Error revoking blob URL:', e);
          }
        }
      });
      
      // Clear the profile picture URL ref to avoid stale references
      profilePictureUrl.value = '';
    });

    const portPermissions = usePortPermissions();

    return {
      userDisplayName,
      fileInput,
      profilePicture,
      isProfilePicLoading,
      entityCounts,
      isLoadingCounts,
      isUploading,
      profilePictureUrl,
      defaultAvatar,
      triggerFileInput,
      handleFileSelected,
      loadEntityCounts,
      portPermissions
    };
  }
};
</script>

<style scoped>
.chat-avtar {
  position: relative;
}

/* Ensures avatar is always a fixed-size perfect circle */
.profile-avatar {
  width: 90px;
  height: 90px;
  object-fit: cover;
}

.profile-avatar-skeleton {
  width: 90px;
  height: 90px;
}

.profile-pic-upload {
  position: absolute;
  bottom: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
}

.chat-avtar:hover .profile-pic-upload {
  opacity: 1;
}

.profile-pic-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style> 